// Batch 19 · Stage C — installable app helpers (C1 service worker + C2 install page). Pure functions are exported for jest.
export const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";

/** Register the shell service worker (production builds only; the dev server has no sw.js of its own). */
export function registerServiceWorker(win = typeof window !== "undefined" ? window : null) {
  try {
    if (!win || !("serviceWorker" in win.navigator)) return false;
    if (process.env.NODE_ENV !== "production") return false;
    win.navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(BUILD_ID)}`).catch(() => {});
    return true;
  } catch { return false; }
}

/** Sign-out: every cache this app made (the shell caches) and every stored value of this origin are removed. */
export async function clearAppCaches(win = typeof window !== "undefined" ? window : null) {
  let n = 0;
  try {
    if (win && win.caches) {
      const keys = await win.caches.keys();
      for (const k of keys) { if (await win.caches.delete(k)) n += 1; }
    }
  } catch { /* no cache API */ }
  try { win?.navigator?.serviceWorker?.controller?.postMessage({ type: "CLEAR" }); } catch { /* noop */ }
  return n;
}
export function clearLocalState(win = typeof window !== "undefined" ? window : null) {
  try { win?.localStorage?.clear(); } catch { /* noop */ }
  try { win?.sessionStorage?.clear(); } catch { /* noop */ }
}

// ---- the browser's install prompt (Chrome / Edge on desktop and Android)
let deferredPrompt = null;
const listeners = new Set();
export function captureInstallPrompt(win = typeof window !== "undefined" ? window : null) {
  if (!win) return;
  win.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; listeners.forEach((f) => f(true)); });
  win.addEventListener("appinstalled", () => { deferredPrompt = null; listeners.forEach((f) => f(false)); });
}
export function canPromptInstall() { return !!deferredPrompt; }
export function onInstallPromptChange(f) { listeners.add(f); return () => listeners.delete(f); }
export async function promptInstall() {
  if (!deferredPrompt) return null;
  const p = deferredPrompt; deferredPrompt = null;
  try { p.prompt(); const r = await p.userChoice; return r?.outcome || "unknown"; } catch { return null; }
}
export function _setDeferredPromptForTests(v) { deferredPrompt = v; }

/** True when the page runs as an installed app (standalone window). */
export function isStandalone(win = typeof window !== "undefined" ? window : null) {
  try {
    if (!win) return false;
    if (win.navigator && win.navigator.standalone === true) return true;              // iOS Safari
    return !!(win.matchMedia && win.matchMedia("(display-mode: standalone)").matches);
  } catch { return false; }
}

/** Which install path this device gets. Pure: user agent + touch points → kind. */
export function detectDevice(ua = "", { maxTouchPoints = 0 } = {}) {
  const s = String(ua || "");
  const isIOS = /iPhone|iPad|iPod/i.test(s) || (/Macintosh/i.test(s) && maxTouchPoints > 1);   // iPadOS reports "Macintosh"
  if (isIOS) return { kind: "ios", browser: /CriOS/i.test(s) ? "chrome" : /FxiOS/i.test(s) ? "firefox" : "safari" };
  const isAndroid = /Android/i.test(s);
  const isEdge = /Edg\//i.test(s);
  const isChrome = /Chrome\//i.test(s) && !/OPR\//i.test(s);
  const isSafari = /Safari\//i.test(s) && !isChrome && !isEdge && !/Firefox\//i.test(s);
  if (isAndroid) return { kind: "android", browser: isEdge ? "edge" : isChrome ? "chrome" : isSafari ? "samsung-or-other" : "other" };
  if (/Macintosh/i.test(s) && isSafari) return { kind: "mac-safari", browser: "safari" };
  if (isChrome || isEdge) return { kind: "desktop-chromium", browser: isEdge ? "edge" : "chrome" };
  return { kind: "desktop-other", browser: /Firefox\//i.test(s) ? "firefox" : "other" };
}
